import { useUI } from '$store/sdk/useUI.ts'
import {
    mapCategoriesToAnalyticsCategories,
    mapProductCategoryToAnalyticsCategories,
} from '$store/utils/productToAnalyticsItem.ts'
import { useSignal } from '@preact/signals'
import type { AnalyticsItem, Product, ProductDetailsPage } from 'apps/commerce/types.ts'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import { useCallback } from 'preact/hooks'

import { sendEvent } from '$store/sdk/analytics.ts'

export interface Options {
    item_list_id?: string
    item_list_name?: string
    itemsToAdd: {
        skuId: string
        sellerId?: string
        price: number
        discount: number
        breadcrumbListItems?: ProductDetailsPage['breadcrumbList']['itemListElement']
        url: Product['url']
        productID: Product['productID']
        listPrice: number
        brand: Product['brand']
        category: Product['category']
        /**
         * product id
         */
        productGroupID?: string
        /**
         * product name
         */
        productGroupName?: string
        /**
         * sku name
         */
        name: string
        productGroupId?: string
    }[]
}

export const useAddToCart = ({ itemsToAdd, item_list_id, item_list_name }: Options) => {
    const isAddingToCart = useSignal(false)
    const { displayCart } = useUI()
    const { addItems, loading, cart } = useCart()

    const onClick = useCallback(
        async (e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()

            try {
                isAddingToCart.value = true

                const parsedItems = itemsToAdd.map((item) => ({
                    id: item.skuId,
                    seller: item.sellerId ?? '1',
                    quantity: 1,
                }))

                await addItems({
                    orderItems: parsedItems,
                })

                const analyticsItems = itemsToAdd.map((item) => {
                    const {
                        breadcrumbListItems,
                        category,
                        productGroupID,
                        productID,
                        price,
                        listPrice,
                        productGroupName,
                        brand,
                        name,
                        skuId,
                    } = item

                    const categories = breadcrumbListItems
                        ? mapCategoriesToAnalyticsCategories(
                            breadcrumbListItems
                                .map(({ name: _name }) => _name ?? '')
                                .filter(Boolean) ?? [],
                        )
                        : mapProductCategoryToAnalyticsCategories(category ?? '')

                    const discount = Number((price && listPrice ? listPrice - price : 0).toFixed(2))

                    return {
                        item_id: `${productGroupID ?? 'error-002'}_${skuId}`,
                        quantity: 1,
                        coupon: '',
                        price: price ? price - discount : undefined, // Necessário devido à auto "correção" que o app da Sourei faz
                        index: -1, // Necessário devido à auto "correção" que o app da Sourei faz
                        discount,
                        item_name: productGroupName ?? name ?? '',
                        item_variant: name,
                        item_brand: brand?.name,
                        item_list_id: item_list_id ?? '',
                        item_list_name: item_list_name ?? '',
                        ...categories,
                    } as AnalyticsItem
                })

                const productsValueSum = itemsToAdd.reduce(
                    (acc, { price }) => acc + (price ?? 0),
                    0,
                )

                sendEvent({
                    name: 'add_to_cart',
                    params: {
                        currency: cart.value?.storePreferencesData.currencyCode,
                        value: productsValueSum,
                        items: analyticsItems,
                    },
                })

                displayCart.value = true
            } finally {
                isAddingToCart.value = false
            }
        },
        [itemsToAdd],
    )

    return { onClick, disabled: loading.value, loading: isAddingToCart.value }
}

// Arquivo de interesse para o rollback da demanda #4561
// Este é um dos arquivos que foram (ou que poderão ser) alterados 
// para realização do rollback